<template>
  <sidebar-layout
    :page-title="pageData.pageTitle"
    :description="pageData.description"
  >
    <div
      v-if="pageData.warning"
      v-dompurify-html="pageData.warning"
      class="warning"
    />

    <form>
      <pins-section
        :pax="pinsData"
        :form-errors="formErrors.pinsData"
        :title="pageData.pinsCardTitle"
        :description="pageData.pinsCardDescription"
        :validation="$v"
      />
      <flight-section
        :trip-type="flight.tripType"
        :travel-class="flight.travelClass"
        :title="pageData.flightDataTitle"
        :description="pageData.flightDataDescription"
        :klaf-url="pageData.klafDestinationsUrl"
        :form-errors="formErrors"
        :validation="$v"
      />
      <flight-date-section
        :flight="flight"
        :trip-type="flight.tripType"
        :title="pageData.flightDateTitle"
        :description="pageData.flightDateDescription"
        :form-errors="formErrors.flight"
        :validation="$v"
      />
      <pax-section
        :title="pageData.paxTitle"
        :description="pageData.paxDescription"
        :form-errors="formErrors"
        :validation="$v"
      />
    </form>

    <div class="button-wrap">
      <terms-and-conditions
        v-if="pageData.termsAndConditionContent"
        :open-modal-on-init="pageData.showTCModal"
        :content="pageData.termsAndConditionContent"
        :text="pageData.termsAndConditionLinkText"
        :title="pageData.termsAndConditionTitle"
      />
      <blue-button
        class="button"
        :label="$t('pinsReward.submitRequest')"
        @clicked="submitForm()"
      />
    </div>
  </sidebar-layout>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import FormMixin from 'components/Forms/FormMixin'
  import FormHelpers from 'components/Forms/FormHelpers'

  export default {
    components: {
      'sidebar-layout': () =>
        import(/* webpackChunkName: "sidebar-layout" */ 'layouts/SidebarLayout'),
      'pins-section': () =>
        import(
          /* webpackChunkName: "reward-form-pins" */ 'containers/Pages/RewardRequest/FormComponents/PinsSection'
        ),
      'flight-section': () =>
        import(
          /* webpackChunkName: "reward-form-flight" */ 'containers/Pages/RewardRequest/FormComponents/FlightSection'
        ),
      'flight-date-section': () =>
        import(
          /* webpackChunkName: "reward-form-flight-date" */ 'containers/Pages/RewardRequest/FormComponents/FlightDateSection'
        ),
      'pax-section': () =>
        import(
          /* webpackChunkName: "reward-form-flight-date" */ 'containers/Pages/RewardRequest/FormComponents/PaxSection'
        ),
      'terms-and-conditions': () =>
        import(
          /* webpackChunkName: "terms-and-conditions" */ 'containers/Pages/RewardClassUpgrade/FormComponents/TermsAndConditions'
        ),
      'blue-button': () =>
        import(/* webpackChunkName: "btn-blue" */ 'components/Buttons/Blue')
    },

    mixins: [FormMixin, FormHelpers],

    computed: {
      ...mapState('rewardRequest', [
        'pageData',
        'pinsData',
        'flight',
        'searchForm',
        'flightDate',
        'paxSelector',
        'paxList',
        'formErrors'
      ]),
      ...mapGetters('device', ['isMobile']),
      ...mapGetters('rewardRequest', ['$v'])
    },
    methods: {
      submitForm() {
        this.$store
          .dispatch('rewardRequest/validate')
          .then(() => {
            const title = 'payments.popupMessages.success.successTitle'
            const message = 'pinsReward.successMessage'
            this.$_formMixin_successModal(title, message, true)
            this.$store.dispatch('rewardRequest/clearForm')
          })
          .catch((err) => {
            if (typeof err.type !== 'undefined' && err.type === 'submit') {
              if (err.message) {
                this.$_formMixin_openFailModal(err.message)
              } else {
                this.$_formMixin_openFailModal(err.msg)
              }
            }

            this.$nextTick(() => {
              this.$_formHelpers_scrollToErrorIeFriendly(this.$el, 'error', [
                '.form-section'
              ])
            })
          })
          .finally(() => {
            this.$_formMixin_enableScroll(this.deviceType)
          })
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  form {
    background-color: $blue-bg-css;
  }

  .warning {
    padding: 16px 18px;
    margin: 0 20px 23px;
    border-left: 3px solid $orange;
    border-radius: 6px;
    background-color: $warning-bg;
    color: $blue-dark;
    line-height: 20px;

    @include bp-mf-desktop {
      margin: 0 0 23px;
    }

    ::v-deep p {
      margin: 0;
    }
  }

  ::v-deep .field-group {
    input,
    textarea {
      border: 1px solid $blue-border !important;
      border-radius: 4px;
      color: $blue-dark !important;

      @include input-placeholder {
        color: $blue-secondary !important;
      }
    }

    input {
      height: 56px !important;
    }

    #comment {
      height: 56px;
      min-height: 56px;
      max-height: 150px;
      min-width: 100%;
      max-width: 100%;
    }

    &.error input {
      border-color: $red-dark !important;
    }
  }

  .button-wrap {
    display: flex;
    flex-direction: column;
    padding: 32px 20px;

    @include bp-mf-tablet {
      align-items: center;
      flex-direction: row;
      justify-content: space-between;
      padding: 40px 0 0;
    }
  }

  .btn-blue {
    @include bp-mf-tablet {
      margin-left: auto;
    }
  }
</style>
